import React, { useEffect } from "react";
import AuthenticatingIndicator from "@/pages/components/authenticat-ingIndicator";
import { useLoaderData, useNavigate } from "react-router-dom";
import type { ValidateTokenResult } from "@/services/validator";

import type { IAuthData } from "@/context/auth-provider";
import useAuth, { AuthMode } from "@/context/auth-provider";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useGroupProvider } from "@/context/group-provider";

import useGroup from "@/hooks/use-group";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import { useNotificationError } from "@/context/error-provider";
import { getFlavourFor } from "@/config/product-config";
import { useTheme } from "@/context/theme-provider";
import { STORAGE, STORAGE_KEY } from "@/constants";
import { setItem } from "@/lib/storage-utils";

const GuestAppLoader: React.FC = () => {
  const navigate = useNavigate();
  const { changeTheme } = useTheme();
  const { updateAuthData } = useAuth();
  const { groupData, setGroupData } = useGroupProvider();
  const loginResponse = useLoaderData() as ValidateTokenResult;
  const groupQuery = useGroup();
  const { addError } = useNotificationError();

  // Step 1: Validate the guest token. This is handled via loader function in route-guest-config.tsx

  useEffect(() => {
    if (loginResponse.error) {
      navigate("/error", {
        replace: true,
        state: { error: loginResponse.error },
      });
    } else {
      document.body.classList.toggle("embedded", loginResponse.data.isEmbedded);

      // Step 2: Set the auth data and user type: guest
      const authData: IAuthData = {
        user: loginResponse?.data,
        mode:
          loginResponse?.data.route === ScreenRouteNames.HOME.path
            ? loginResponse?.data?.mode?.toLowerCase()
            : AuthMode.GUEST,
      };
      updateAuthData(authData);

      //Step 3: Get the group data
      if (groupQuery.isPending) return; //protect multiple calls for same api
      groupQuery.mutate(loginResponse.data.loginGroupId, {
        onSuccess: (data) => {
          if (data) {
            setGroupData(data.data.values);
            const flavour = getFlavourFor(
              data.data?.values?.group_id,
              data.data?.values?.division_id,
            );
            if (flavour) {
              changeTheme(flavour.theme);
              (
                window as Window & { __PRODUCT_CONFIG__: unknown }
              ).__PRODUCT_CONFIG__ = flavour;
              setItem(STORAGE_KEY.PRODUCT_CONFIG, flavour, STORAGE.SESSION);
            }
          } else {
            addError({
              message: data.summary.status_message,
              type: ErrorNotificationType.ERROR,
            });
          }
        },
        onError: (error) => {
          addError({
            message: error.message,
            type: ErrorNotificationType.ERROR,
          });
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginResponse]);

  //Step 4: Get the demographics data, global flags data, provider network data,
  useEffect(() => {
    if (groupData) {
      // console.log("Guest App Loader:-> logged", loginResponse.data);
      navigate(`/${loginResponse.data.route}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData]);

  return <AuthenticatingIndicator />;
};

export default GuestAppLoader;
